/**
 * Vault domain types — expense tracking, scholarships, and endowments.
 *
 * The Vault screen models the financial side of the program: contestant
 * expense submissions and reimbursements, scholarship awards, and the
 * long-running endowments that fund them.
 *
 * `ContestantId` is intentionally redeclared here (rather than imported
 * from `merit.ts`) so each domain module is independent. The brand
 * string ('ContestantId') matches the merit module so structural
 * equivalence holds at the type level if the two modules ever interop.
 *
 * Naming conventions (Requirement 16.6):
 *   - PascalCase for types/interfaces
 *   - branded `Id` aliases for entity identifiers
 *   - `Status` union types where applicable
 *   - module-level mock data exported as `MOCK_<DOMAIN>_<COLLECTION>`
 *
 * Validates: Requirements 12.9, 16.1, 16.6
 */

import type { Brand } from './shared/branded';

// -----------------------------------------------------------------------------
// Branded identifiers
// -----------------------------------------------------------------------------

export type ExpenseId = Brand<string, 'ExpenseId'>;
export type ScholarshipId = Brand<string, 'ScholarshipId'>;
export type EndowmentId = Brand<string, 'EndowmentId'>;
// Locally redeclared so this module does not depend on merit.ts.
export type ContestantId = Brand<string, 'ContestantId'>;

// -----------------------------------------------------------------------------
// Domain types
// -----------------------------------------------------------------------------

export type ExpenseStatus = 'pending' | 'approved' | 'rejected' | 'reimbursed';

export interface Expense {
  id: ExpenseId;
  contestantId: ContestantId;
  amount: number;
  category: string;
  description: string;
  submittedAt: string;
  status: ExpenseStatus;
  receiptUrl?: string;
}

export interface Scholarship {
  id: ScholarshipId;
  name: string;
  sponsor: string;
  amount: number;
  awardedTo?: ContestantId;
  awardedAt?: string;
}

export interface Endowment {
  id: EndowmentId;
  name: string;
  totalValue: number;
  annualPayout: number;
  sourceYear: number;
}

export interface VaultData {
  expenses: Expense[];
  scholarships: Scholarship[];
  endowments: Endowment[];
}

// -----------------------------------------------------------------------------
// Mock data
// -----------------------------------------------------------------------------

export const MOCK_VAULT_EXPENSES: Expense[] = [
  {
    id: 'e-1' as ExpenseId,
    contestantId: 'c-1' as ContestantId,
    amount: 425.0,
    category: 'wardrobe',
    description: 'Evening gown alterations.',
    submittedAt: '2025-08-22T19:00:00.000Z',
    status: 'reimbursed',
    receiptUrl: 'https://receipts.example/e-1.pdf',
  },
  {
    id: 'e-2' as ExpenseId,
    contestantId: 'c-2' as ContestantId,
    amount: 180.5,
    category: 'travel',
    description: 'Mileage to and from regional appearances.',
    submittedAt: '2025-09-01T15:30:00.000Z',
    status: 'approved',
  },
  {
    id: 'e-3' as ExpenseId,
    contestantId: 'c-3' as ContestantId,
    amount: 95.0,
    category: 'media',
    description: 'Headshot session retouching fee.',
    submittedAt: '2025-09-04T17:45:00.000Z',
    status: 'pending',
  },
  {
    id: 'e-4' as ExpenseId,
    contestantId: 'c-1' as ContestantId,
    amount: 1200.0,
    category: 'wardrobe',
    description: 'Custom interview suit.',
    submittedAt: '2025-09-10T20:00:00.000Z',
    status: 'rejected',
  },
  {
    id: 'e-5' as ExpenseId,
    contestantId: 'c-4' as ContestantId,
    amount: 65.25,
    category: 'supplies',
    description: 'Talent rehearsal materials.',
    submittedAt: '2025-09-12T14:20:00.000Z',
    status: 'pending',
    receiptUrl: 'https://receipts.example/e-5.jpg',
  },
  {
    id: 'e-6' as ExpenseId,
    contestantId: 'c-2' as ContestantId,
    amount: 310.0,
    category: 'travel',
    description: 'Hotel night before finals.',
    submittedAt: '2025-09-11T23:00:00.000Z',
    status: 'approved',
    receiptUrl: 'https://receipts.example/e-6.pdf',
  },
];

export const MOCK_VAULT_SCHOLARSHIPS: Scholarship[] = [
  {
    id: 'sch-1' as ScholarshipId,
    name: 'Reignara Leadership Scholarship',
    sponsor: 'Reignara Salon',
    amount: 5000,
    awardedTo: 'c-1' as ContestantId,
    awardedAt: '2025-09-12T22:30:00.000Z',
  },
  {
    id: 'sch-2' as ScholarshipId,
    name: 'Treasure Valley Service Award',
    sponsor: 'Treasure Valley Foods',
    amount: 2500,
    awardedTo: 'c-2' as ContestantId,
    awardedAt: '2025-09-12T22:32:00.000Z',
  },
  {
    id: 'sch-3' as ScholarshipId,
    name: 'Snake River Academic Grant',
    sponsor: 'Snake River Jewelers',
    amount: 1500,
  },
  {
    id: 'sch-4' as ScholarshipId,
    name: 'Founders Memorial Scholarship',
    sponsor: 'Idaho America Pageants Foundation',
    amount: 2000,
    awardedTo: 'c-3' as ContestantId,
    awardedAt: '2025-09-12T22:34:00.000Z',
  },
];

export const MOCK_VAULT_ENDOWMENTS: Endowment[] = [
  {
    id: 'en-1' as EndowmentId,
    name: 'Founders Endowment',
    totalValue: 250000,
    annualPayout: 10000,
    sourceYear: 2010,
  },
  {
    id: 'en-2' as EndowmentId,
    name: 'Reignara Legacy Fund',
    totalValue: 125000,
    annualPayout: 6000,
    sourceYear: 2018,
  },
  {
    id: 'en-3' as EndowmentId,
    name: 'Idaho Service Endowment',
    totalValue: 75000,
    annualPayout: 3500,
    sourceYear: 2021,
  },
];

export const MOCK_VAULT_DATA: VaultData = {
  expenses: MOCK_VAULT_EXPENSES,
  scholarships: MOCK_VAULT_SCHOLARSHIPS,
  endowments: MOCK_VAULT_ENDOWMENTS,
};
